/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import Async from "react-select/async"
import FormWrap from "../FormWrap"
import { ValidationRule, FieldErrors, RegisterOptions } from "react-hook-form"
import { selectStyle } from "./style"

export interface iSelectProps {
  name: string
  initialValue?: any
  value?: any
  defaultOptions?: Array<any> | boolean
  isClearable?: boolean
  isSearchable?: boolean
  isMulti?: boolean
  placeholder?: string
  label?: string
  error?: FieldErrors
  validation?: RegisterOptions
  loadOptions: (input: string) => Promise<any>
  onChange?: (prop: string | string[]) => void
  containerClassName?: string
  noPadding?: boolean
}

const AsyncSelectInput: FunctionComponent<iSelectProps> = ({
  defaultOptions,
  isClearable,
  isSearchable,
  isMulti = false,
  placeholder,
  name,
  error,
  onChange,
  loadOptions,
  label,
  value,
  containerClassName = "",
  noPadding = false,
}) => {
  const [_data, setData] = useState<any[]>([])
  const [_value, setValue] = useState<any>(_data.find((x) => x.value === value))
  const [isLoading, setLoading] = useState(true)

  const style = useMemo(() => {
    const s = selectStyle({ isMulti, noPadding, error })
    return s
  }, [isMulti, error])

  const onInputChange = useCallback(
    (data: any) => {
      setValue(data)
      if (Array.isArray(data)) {
        onChange && onChange(data ? data.map((d) => d.value) : [])
      } else {
        onChange && onChange(data ? data.value : isMulti ? [] : "")
      }
    },
    [name],
  )

  useEffect(() => {
    if (Array.isArray(_data) && value) {
      if (isMulti) {
        const array = []
        for (let i = 0; i < value.length; i++) {
          const x = _data.find((d) => d.value === value[i])
          if (x) array.push(x)
        }
        setValue(array)
      } else {
        const x = _data.find((d) => d.value === value)
        setValue(x)
      }
    }
  }, [value, _data])

  const onLoadOptions = async (input: string) => {
    setLoading(true)
    const result = await loadOptions(input)
    if (!_data.length) {
      setData(result)
    }
    setLoading(false)
    return result
  }

  return (
    <div className={`${containerClassName} form-group`}>
      {(label || error) && (
        <label htmlFor={name}>
          {label}
          {error && <span>{error.message}</span>}
        </label>
      )}
      <div className="form-control">
        <Async
          id={name}
          cacheOptions
          defaultOptions={defaultOptions}
          isClearable={isClearable}
          isSearchable={isSearchable}
          isMulti={isMulti}
          value={_value}
          isLoading={isLoading}
          placeholder={placeholder}
          loadOptions={onLoadOptions}
          classNamePrefix={"react-select"}
          menuPlacement="auto"
          className={"select"}
          styles={style}
          onChange={onInputChange}
        />
      </div>
    </div>
  )
}

const MemoSelectInput = memo(AsyncSelectInput, (currentProps, nextProps) => {
  const { initialValue } = currentProps
  const { initialValue: nextInitial } = nextProps

  return (
    initialValue === nextInitial &&
    currentProps.error?.message === nextProps.error?.message &&
    currentProps.defaultOptions === nextProps.defaultOptions
  )
})

// export default MemoSelectInput
export default AsyncSelectInput

export const FormAsyncSelectInput = FormWrap<iSelectProps>(MemoSelectInput)
